import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class extends Controller<HTMLElement> {
  static targets = ["source", "error"];
  static values = { url: String };
  declare readonly sourceTarget: HTMLElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly urlValue: string;
  private observer?: MutationObserver;
  private request?: AbortController;

  connect(): void {
    this.observer = new MutationObserver(() => this.connectionChanged());
    this.observer.observe(this.sourceTarget, { attributes: true, attributeFilter: ["connected"] });
    this.connectionChanged();
  }

  disconnect(): void {
    this.observer?.disconnect();
    this.request?.abort();
  }

  private connectionChanged(): void {
    if (this.sourceTarget.hasAttribute("connected")) void this.sync();
  }

  async sync(): Promise<void> {
    this.request?.abort();
    const request = this.request = new AbortController();
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "text/html", "Turbo-Frame": "assistant_workspace" },
        credentials: "same-origin", cache: "no-store", signal: request.signal,
      });
      if (!response.ok) throw new Error("Conversation sync failed");
      const document = new DOMParser().parseFromString(await response.text(), "text/html");
      const snapshot = document.getElementById(this.element.id);
      if (!snapshot) throw new Error("Conversation no longer available");
      if (request.signal.aborted || !this.element.isConnected) return;
      // Reuse versioned stream upserts; never replace the composer or its files.
      snapshot.querySelectorAll<HTMLElement>('[data-assistant-timeline-target~="entry"], [data-assistant-composer-target~="status"]')
        .forEach((entry) => this.restore(entry));
      this.errorTarget.hidden = true;
    } catch {
      if (!request.signal.aborted && this.element.isConnected) this.errorTarget.hidden = false;
    }
  }

  private restore(entry: HTMLElement): void {
    const message = entry.matches('[data-assistant-timeline-target~="entry"]');
    // Both versions are server-owned (data-status-version comes from Assistant::TurnState#version).
    const version = message ? "timelineVersion" : "statusVersion";
    const current = document.getElementById(entry.id);
    if (current && current.dataset[version] === entry.dataset[version]) return;

    const stream = document.createElement("turbo-stream");
    stream.setAttribute("action", message ? "append" : "replace");
    stream.setAttribute("target", message ? `assistant_messages_${entry.dataset.timelineChat}` : entry.id);
    const template = document.createElement("template");
    template.content.append(entry.cloneNode(true));
    stream.append(template);
    Turbo.renderStreamMessage(stream.outerHTML);
  }
}
